import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { getShortlists } from '@/api/requests/shortlists';
import { AppDispatch, RootState } from '@/slices/store';
import log from '@/utils/logger';

interface ShortlistState {
  shortlists: any[] | null;
  loading: boolean;
  error: string | null;
}

const initialState: ShortlistState = {
  shortlists: null,
  loading: false,
  error: null,
};

export const fetchShortlistsThunk = createAsyncThunk<
  any[],
  string,
  { state: RootState; dispatch: AppDispatch; rejectValue: string }
>('shortlists/fetchShortlists', async (organizationId, { getState, dispatch, rejectWithValue }) => {
  const state = getState();

  if (state.shortlists.loading) {
    log.info('Another Shortlists fetch is already in progress, skipping this one.');
    return;
  }

  dispatch(shortlistsSlice.actions.setLoading(true));

  try {
    const shortlists = await getShortlists(organizationId);
    localStorage.setItem('shortlists', JSON.stringify(shortlists));
    return shortlists;
  } catch (error: any) {
    return rejectWithValue(error.response?.data?.message || 'Failed to fetch shortlists');
  } finally {
    dispatch(shortlistsSlice.actions.setLoading(false));
  }
});

const shortlistsSlice = createSlice({
  name: 'shortlists',
  initialState,
  reducers: {
    setLoading: (state, action) => {
      state.loading = action.payload;
    },
    loadShortlistsFromStorage: (state) => {
      const storedShortlists = localStorage.getItem('shortlists');
      if (storedShortlists) {
        state.shortlists = JSON.parse(storedShortlists);
      }
    },
    clearShortlists: (state) => {
      state.shortlists = null;
      state.error = null;
      localStorage.removeItem('shortlists');
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchShortlistsThunk.fulfilled, (state, action) => {
        state.shortlists = action.payload;
      })
      .addCase(fetchShortlistsThunk.rejected, (state, action) => {
        state.error = action.payload || 'Failed to fetch shortlists';
      });
  },
});

export const { loadShortlistsFromStorage, clearShortlists, setLoading } = shortlistsSlice.actions;
export default shortlistsSlice.reducer;
